/**
 * f15.5: the artifact's opaque external link, on the internal detail screen.
 *
 * Shown to everyone who can see the artifact -- the link alone grants nothing;
 * an access code does. The URL is built server-side from `PUBLIC_APP_URL` and
 * the artifact's UUID, and names no client, title or structure.
 */

import { useEffect, useState } from 'react';
import { Copy, ExternalLink as ExternalLinkIcon } from 'lucide-react';

import type { ArtifactDetail } from '@/api/client';

import '@/styles/external-viewer.css';

export function PreviewExternalPageButton({ artifact }: { artifact: ArtifactDetail }) {
  return (
    <a className="btn btn-secondary" href={artifact.external_url} target="_blank" rel="noopener noreferrer">
      <ExternalLinkIcon aria-hidden="true" /> Preview external page
    </a>
  );
}

export function ExternalLinkRow({ artifact }: { artifact: ArtifactDetail }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const copy = () => {
    void navigator.clipboard.writeText(artifact.external_url).then(() => setCopied(true));
  };

  return (
    <div className="external-link-row">
      <span className="external-link-label">External link</span>
      <code className="mono external-link-url">{artifact.external_url}</code>
      <button type="button" className="icon-btn" title="Copy link" aria-label="Copy link" onClick={copy}>
        <Copy aria-hidden="true" />
      </button>
      {artifact.published_state !== 'PUBLISHED' && (
        <span className="hint">Not available until a version is published.</span>
      )}
      {copied && (
        <div className="toast" role="status">
          Link copied
        </div>
      )}
    </div>
  );
}
